import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { HiArrowLeft } from "react-icons/hi";
import { TfiLayersAlt } from "react-icons/tfi";
import { useNavigate, useParams } from "react-router-dom";

import { Tooltip as ReactTooltip } from "react-tooltip";

import VideoPlayer from "../home/shared/VideoPlayer";
import GeoLocationPopup from "../videoPlayer/shared/GeoLocationPopup";
import { TabNames } from "constant/tabNames";
import Episodes from "../videoPlayer/Episodes";
import MovieTabs from "../videoPlayer/MovieTabs";
import SourceInfo from "../videoPlayer/SourceInfo";
import TVSeriesTabs from "../videoPlayer/TVSeriesTabs";
import { RouteName } from "constant/routeNames";
import PopUpModel from "components/PopUpModel";
import LoadingSpin from "components/Loading spins/LoadingSpin";
import VideoPlayerLeftSidePanel from "../videoPlayer/VideoPlayerLeftSidePanel";
import PremiumContentModal from "components/PremiumContentModal";
import SubscriptionService from "services/subscription/subscriptionService";

function CommonPlayerSection({
  videoDetails,
  contentType,
  openGeoLoactionModel,
  setOpenGeoLoactionModel,
  error,
  searchPage,
  currentTime,
}: any) {
  const FE_URL = process.env.REACT_APP_FRONTEND_URL;
  const [exploreSeries, setExploreSeries] = useState(false);
  const navigate = useNavigate();
  const { slug } = useParams();
  const cleanedSlug = slug?.endsWith(".html") ? slug.slice(0, -5) : slug;
  const loggedInUser = useSelector((state: any) => state?.user?.currentUser);
  const planId = Number(videoDetails?.plan_id);
  const contentReady = !!videoDetails?.id && videoDetails?.slug === cleanedSlug;
  const [access, setAccess] = useState<{
    slug: string | undefined;
    planId: number;
    user: any;
    allowed: boolean;
  } | null>(null);
  const accessResolved = contentReady && access?.slug === cleanedSlug &&
    Object.is(access?.planId, planId) && access?.user === loggedInUser;
  const canPlay = accessResolved && access?.allowed === true;
  const premiumBlocked = accessResolved && !access?.allowed;

  useEffect(() => {
    if (!contentReady) return;
    let cancelled = false;
    const resolveAccess = async () => {
      let allowed = planId !== 2;
      if (planId === 2 && loggedInUser && cleanedSlug) {
        try {
          const res = await SubscriptionService.checkContentAccessBySlug(cleanedSlug);
          allowed = !!res?.status && res?.data?.has_access === true;
        } catch {
          allowed = false;
        }
      }
      if (!cancelled) {
        setAccess({ slug: cleanedSlug, planId, user: loggedInUser, allowed });
      }
    };
    resolveAccess();
    return () => { cancelled = true; };
  }, [contentReady, cleanedSlug, planId, loggedInUser]);
  const [selectedTab, setSelectedTab] = useState("");
  const { isFetching } = useSelector((state: any) => state?.video);
  const [height, setHeight] = useState<number>();
  // const scrollToTopRef = useRef<HTMLDivElement | null>(null);

  // useEffect(() => {
  //   const timeoutId = setTimeout(() => {
  //     window.scrollTo({ top: 0, behavior: "smooth" });
  //   }, 2);

  //   return () => {
  //     clearTimeout(timeoutId);
  //   };
  // }, []);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const navigateBack = () => {
    if (!exploreSeries) {
      if (slug) {
        if (searchPage) {
          navigate(-1);
        } else {
          const state = { playerPage: true };
          navigate(RouteName.VideoDetailRoute.replace(":slug", slug), {
            state,
          });
        }
      }
    }
  };

  // geo location function
  const closeModal = () => {
    setOpenGeoLoactionModel(false);
    // navigate(RouteName.VideoDetailRoute.replace(":slug", videoDetails?.slug));
    navigate(RouteName.Video.replace("/*", ""));
  };

  useEffect(() => {
    setSelectedTab(
      videoDetails.no_of_seasons !== undefined
        ? TabNames.Episodes
        : TabNames.Info
    );
  }, [videoDetails]);

  const heightHalfGenerator = () => {
    const aspectRatio = 16 / 9;
    const maxWidth = window.innerWidth;
    const halfHeight = maxWidth / aspectRatio;

    setHeight(halfHeight);
  };


  useEffect(() => {
    const handleResize = () => {
      heightHalfGenerator();
    };

    heightHalfGenerator();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>
          {videoDetails?.seo_title ? `${videoDetails?.seo_title}` : "AYOZAT"}
        </title>
        <meta name="title" content={`${videoDetails?.seo_title}`} />
        <meta
          name="description"
          content={
            videoDetails?.description?.length > 100
              ? `${videoDetails?.description.slice(0, 100)}...`
              : videoDetails?.description
          }
        />
        {/* <meta property="og:image" content={videoDetails?.thumbnail_url} /> */}
        <meta name="keywords" content={videoDetails?.focus_keyword} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${videoDetails?.title} - AYOZAT`}
        />
        <meta name="twitter:image" content={videoDetails?.thumbnail_url} />
        <meta
          name="twitter:description"
          content={
            videoDetails?.description?.length > 100
              ? `${videoDetails?.description.slice(0, 100)}...`
              : videoDetails?.description
          }
        />
        <meta
          name="twitter:url"
          content={`${FE_URL}/watch/${videoDetails?.slug || ""}`}
        />
        {/* <meta name="twitter:image" content={videoDetails?.thumbnail_url} /> */}

        <meta property="og:title" content={`${videoDetails?.title} - AYOZAT`} />
        <meta
          property="og:description"
          content={
            videoDetails?.description?.length > 100
              ? `${videoDetails?.description.slice(0, 100)}...`
              : videoDetails?.description
          }
        />
        <meta property="og:image" content={videoDetails?.thumbnail_url} />
        <meta
          property="og:url"
          content={`${FE_URL}/watch/${videoDetails?.slug || ""}`}
        />
        <meta property="og:type" content="website" />
        <link
          rel="canonical"
          href={`${FE_URL}/watch/${videoDetails?.slug || ""}`}
        />
      </Helmet>

      {!isFetching ? (
        <VideoPlayerLeftSidePanel
          isActive={exploreSeries}
          videoDetails={videoDetails && videoDetails}
          loading={isFetching}
          onClose={() => setExploreSeries(false)}
        />
      ) : (
        <></>
      )}

      <h1 className="hidden">AYOZAT Video Player</h1>

      <div className={`fixed z-50 flex  justify-between w-full px-10 mt-0`}>
        <div className="!z-10 flex items-center mt-10 opacity-30 hover:opacity-100 duration-500 max-sm:opacity-100">
          {/* back button */}
          <button
            className={`!z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-black/60 text-lg text-white shadow-[0_4px_16px_rgba(0,0,0,0.4)] backdrop-blur-md transition-all duration-300 hover:bg-black/80 max-sm:ml-[-20px] max-sm:mt-[-20px] max-sm:scale-[80%] ${
              exploreSeries
                ? "cursor-default opacity-50"
                : "cursor-pointer hover:scale-105"
            }`}
            onClick={navigateBack}
          >
            <HiArrowLeft />
          </button>

          {/* explore toggle pill */}
          <div
            className={`ml-3 flex items-center overflow-hidden rounded-full border backdrop-blur-md transition-all duration-300 ease-out max-md:hidden ${
              exploreSeries
                ? "border-[#F50537]/40 bg-[#F50537] shadow-[0_4px_16px_rgba(245,5,55,0.35)]"
                : "border-white/10 bg-black/60 hover:border-white/25"
            }`}
          >
            <button
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg text-white"
              onClick={() => setExploreSeries((val) => !val)}
              data-tooltip-id={`AiOutlineInfoCircle - ${contentType}`}
            >
              {contentType === "movie" ? (
                <AiOutlineInfoCircle />
              ) : (
                <TfiLayersAlt />
              )}
            </button>
            <span
              className={`whitespace-nowrap pr-4 text-[13px] font-semibold text-white transition-all duration-300 ease-out ${
                exploreSeries
                  ? "max-w-[160px] opacity-100"
                  : "max-w-0 opacity-0"
              }`}
            >
              Explore {contentType === "movie" ? "movie" : "series"}
            </span>
          </div>
        </div>
      </div>
      <div
        className="w-[99.98%] mx-auto overflow-hidden md:h-screen sm:bg-black"
        // ref={mainDivRef}
        // ref={scrollToTopRef}
      >
        <div className="flex justify-center mx-auto overflow-hidden">
          {!isFetching && !openGeoLoactionModel && canPlay ? (
            <VideoPlayer
              videoDetails={videoDetails}
              currentTime={currentTime}
            />
          ) : (
            <div
              className={`flex flex-col items-center justify-center  max-md:mt-10 md:h-[100vh] max-sm:h-[250px] max-md:h-[500px]`}
            >
              <LoadingSpin fontSize={"60px"} color={"#f30543"} />
              {error ? (
                <div className="flex flex-col items-center justify-center mx-auto mt-5 ">
                  <p className="text-base text-gray-500">{error}</p>
                  <button
                    className="p-2 mt-5 text-white bg-red-700 rounded-md"
                    onClick={navigateBack}
                  >
                    Try Again!
                  </button>
                </div>
              ) : (
                <></>
              )}
            </div>
          )}

          {/* <VideoPlayer cdn="https://vz-6c38baf6-966.b-cdn.net/0d99cc81-3b20-411a-a0ea-46206df7aefa/playlist.m3u8" /> */}
        </div>

        {/* geo location popup */}
        {openGeoLoactionModel && (
          <div className="z-50">
            <PopUpModel
              isOpen={openGeoLoactionModel}
              onClose={closeModal}
              width={"800px"}
              showCloseButton={false}
            >
              <GeoLocationPopup onClose={closeModal} />
            </PopUpModel>
          </div>
        )}
      </div>

      <PremiumContentModal
        isOpen={!!premiumBlocked}
        onClose={() => navigate(RouteName.VideoDetailRoute.replace(":slug", slug || ""))}
      />

      {/* In Mobile View */}

      {videoDetails && (
        <div className="relative w-full mt-8 z-100 md:hidden ">
          {/* <VideoPlayerDescMobile /> */}
          <div className="items-center bg-gradient-to-r md:from-black/90 text-gray-100 md:mt-14 !md:w-[600px] md:pl-[80px] px-5 text-left !h-screen justify-center max-sm:w-full">
            {!isFetching ? (
              <>
                <div className="z-50 flex items-center mb-5 md:fixed">
                  {videoDetails.no_of_seasons !== undefined ? (
                    <TVSeriesTabs
                      selectedTab={selectedTab}
                      setSelectedTab={setSelectedTab}
                    />
                  ) : (
                    <MovieTabs
                      selectedTab={selectedTab}
                      setSelectedTab={setSelectedTab}
                    />
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="bg-[#1a1919] animate-pulse flex items-center mb-5 md:fixed w-full h-5"></div>
              </>
            )}
            {selectedTab === TabNames.Episodes && videoDetails && (
              <Episodes videoDetails={videoDetails} loading={isFetching} />
            )}
            {selectedTab === TabNames.Info && videoDetails && (
              <SourceInfo
                videoDetails={videoDetails && videoDetails}
                loading={isFetching}
              />
            )}
          </div>
        </div>
      )}
      {contentType === "movie"
        ? !exploreSeries && (
            <ReactTooltip
              id={`AiOutlineInfoCircle - movie`}
              place="right"
              content={"Info"}
              className="!z-50 bg-slate-700 opacity-100"
            />
          )
        : !exploreSeries && (
            <ReactTooltip
              id={`AiOutlineInfoCircle - tv-series`}
              place="right"
              content={"Episodes and Info"}
              className="!z-50 bg-slate-700 opacity-100"
            />
          )}
    </>
  );
}

export default CommonPlayerSection;
